
import { formatPostalCode } from '@/helpers/string'

/****************************************************************************
 * Charger Location Object Construction
 *
 * TODO: Add data structure specification
 */

/**
 * Custom Constructors per Tenant
 */
const ChargerLocationConstructorsByTenant = {}

/**
 * Select the constructor by Tenant, or use the default
 */
const ChargerLocation = ChargerLocationConstructorsByTenant[process.env.VUE_APP_TENANT]
  /**
  * Turn the plain object into a ChargerLocation object
  */
  || function ChargerLocation({ data, cbapi } = { data: null, cbapi: false }) {
    Object.keys(data).forEach(key => {
      this[key] = data[key]
    })

    this.cbapi = !! cbapi
  }


/****************************************************************************
 * Priority / Phase Information methods
 *
 * Sometimes timeframe in which a Charger Locations is to be realised is determined
 * well ahead. The following methods provide relevant details.
 *
 * The details differ in structure depending on the agreements and / or external systems
 *
 * @typedef {Object} LocationPhaseInformation
 * @property {string} full - The full phase string
 * @property {string|null} year - The year in which the location is to be realized
 * @property {string|null} period - The period of the year in which the location is to be realized
 * @property {string|number|null} priority - The priority rating (within the planned period) of the location
 */

 if (! ChargerLocation.prototype.hasPhaseInformation) {
    /**
     * Whether priority / phase information has been specified
     *
     * @returns {Boolean}
     */
    ChargerLocation.prototype.hasPhaseInformation = function() {
      if (this.meta) {
        return this.meta.phase !== null
      } else {
        let year = this.data?.properties?.proactive_year
        return !! (year && year !== '0')
      }
    }
  }
  if (! ChargerLocation.prototype.getPhaseInformation) {
    /**
     * Returns the Phase information of the Charger Location
     *
     * @returns {LocationPhaseInformation|null}
     */
    ChargerLocation.prototype.getPhaseInformation = function() {
      if (! this.hasPhaseInformation()) return null

      // The CBAPI response was simplified, because the details are not used separately
      if (this.meta) {
        return {
          full: this.meta.phase,
        }
      }

      let year = this.data?.properties?.proactive_year
      let period = this.data?.properties?.proactive_period
      let extra = !! (this.data?.properties?.proactive_extra || false)

      return {
        full: `${year}-${period}${extra ? ' extra' : ''}`,
        year,
        period,
        extra,
        priority: null,
      }
    }
  }

  if (! ChargerLocation.prototype.getFullPaseValue) {

    /**
     * Returns the full phase value of a Charger Location
     *
     * @returns {String}
     */
     ChargerLocation.prototype.getFullPaseValue = function() {
      let info = this.getPhaseInformation()
      return info ? info.full : ''
    }
  }

  /**
   * Location Coordinates
   * @returns {Object}
   */
  ChargerLocation.prototype.getCoordinates = function() {
    if (this.coordinates) {
      return {
        lat: this.coordinates?.lat || null,
        lng: this.coordinates?.lng || null,
      }
    }

    return {
      lat: this?.data?.coordinates?.[1] || null,
      lng: this?.data?.coordinates?.[0] || null,
    }
  }

  ChargerLocation.prototype.hasCoordinates = function() {
    let coords = this.getCoordinates()
    return coords.lat !== null && coords.lng !== null
  }

  /**
   * Whether the location was obtained from CBAPI
   */
  ChargerLocation.prototype.isCBAPI = function() {
    return !! this.cbapi
  }

  ChargerLocation.prototype.getUuid = function() {
    return this.uuid
  }

  ChargerLocation.prototype.getGeographyCode = function() {
    return this.geographyCode || this.data.code || null
  }

  ChargerLocation.prototype.getAddressData = function() {
    return this.address || this.data.address || {}
  }

  ChargerLocation.prototype.getFormattedAddress = function() {
    let address = this.getAddressData()

    if (Object.keys(address).length === 0) {
      return ''
    }

    if (address.streetNumber) {
      let number = (`${address.streetNumber || ''} ${address.streetNumberSuffix || ''}`).trim()
      return (`${address.street || ''} ${number}, ${formatPostalCode(address.postalCode || '')} ${address.city || ''}`).trim().trim(',')
    }

    let number = (`${address.number || ''} ${address.suffix || ''}`).trim()
    return (`${address.street || ''} ${number}, ${formatPostalCode(address.postalcode || '')} ${address.city || ''}`).trim().trim(',')
  }

  ChargerLocation.prototype.getShortAddress = function() {
    let address = this.getAddressData()

    if (address.streetNumber) {
      let number = (`${address.streetNumber || ''} ${address.streetNumberSuffix || ''}`).trim()
      return (`${address.street || ''} ${number}, ${address.city || ''}`).trim().trim(',')
    }

    let number = (`${address.number || ''} ${address.suffix || ''}`).trim()
    return (`${address.street || ''} ${number}, ${address.city || ''}`).trim().trim(',')
  }

  ChargerLocation.prototype.getWorkflowUuid = function() {
    return this?.meta?.workflowUuid || this?.data?.workflowUuid || null
  }

  ChargerLocation.prototype.getStatus = function() {
    return this.status || this?.data?.properties?.status || null
  }

  ChargerLocation.prototype.getSimpleId = function() {
    return this?.meta?.id || this?.data?.properties?.id || null
  }

  ChargerLocation.prototype.getRemark = function() {
    return this?.meta?.remark || this.data?.properties?.remark || null
  }

  ChargerLocation.prototype.getImportSource = function() {
    return this?.meta?.source || this?.dataSource?.value || this?.data?.properties?.source || null
  }
  ChargerLocation.prototype.getConsumerId = function() {
    return this.getGeographyCode() + this.getSimpleId()
  }

  ChargerLocation.prototype.getSyncedTS = function() {
    return this.ts || null
  }

export default ChargerLocation
